import { gReactive } from '@gradin/vue-utils'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '@/functions/api'
import { useDashboardStore } from '@/stores/dashboard'
import { useToastStore } from '@/stores/toast'

export const statusConfig: Record<string, {
  label: string,
  variant: 'primary' | 'success' | 'danger' | 'warning' | 'gray' | 'info',
}> = {
  waiting_payment: {
    label: 'Menunggu Pembayaran',
    variant: 'warning',
  },
  new: {
    label: 'Baru',
    variant: 'info',
  },
  packing: {
    label: 'Diproses',
    variant: 'warning',
  },
  delivering: {
    label: 'Dikirim',
    variant: 'info',
  },
  received: {
    label: 'Diterima',
    variant: 'success',
  },
  completed: {
    label: 'Selesai',
    variant: 'success',
  },
  canceled: {
    label: 'Dibatalkan',
    variant: 'danger',
  },
  complained: {
    label: 'Komplain',
    variant: 'danger',
  },
  complaint_responded: {
    label: 'Komplain Direspon',
    variant: 'warning',
  },
}

export const getOrderStatus = (status: string) => {
  return statusConfig[status] || { label: status, variant: 'gray' as const }
}

export function useShopOrderList () {
  const api = useApi()
  const route = useRoute()
  const router = useRouter()
  const dashboard = useDashboardStore()

  const shopOrders = ref<ShopOrderList[]>()
  const pagination = ref<Pagination>()
  const loading = ref(false)
  const count = ref<ShopOrderListResponse['count']>()

  const getStatus = () => {
    switch (route.query.status as string | undefined) {
      case 'waiting_payment':
        return 'waiting_payment'
      case 'new':
        return 'new'
      case 'packing':
        return 'packing'
      case 'delivering':
        return 'delivering'
      case 'received':
        return 'received'
      case 'canceled':
        return 'canceled'
      case 'complained':
        return 'complained'
      case 'complaint_responded':
        return 'complaint_responded'
      case 'completed':
        return 'completed'
      default:
        return 'new'
    }
  }

  const filterPaymentMethod = computed({
    set: (paymentId?: number) => {
      router.push({
        query: {
          ...route.query,
          page: undefined,
          payment_method: paymentId ?? undefined,
        },
      })
    },
    get: () => (route.query.payment_method ? Number(route.query.payment_method) : undefined),
  })

  const apiParams = computed(() => {
    return {
      page: Number(route.query.page) > 1 ? Number(route.query.page) : undefined,
      status: getStatus(),
      search: route.query.search as string,
      payment_method: route.query.payment_method ? Number(route.query.payment_method) : undefined,
    }
  })

  const getData = async (params = apiParams.value) => {
    try {
      loading.value = true
      const response = await api.GET<ShopOrderListResponse>('admin/shop-order', params)
      shopOrders.value = response.data
      pagination.value = response.meta
      count.value = response.count
      dashboard.setOrdersCountNew(count.value.new)
    } finally {
      loading.value = false
    }
  }

  return {
    shopOrders,
    pagination,
    loading,
    count,
    apiParams,
    filterPaymentMethod,
    getData,
  }
}

export function useShopOrderDetail () {
  const route = useRoute()
  const api = useApi()

  const shopOrder = ref<ShopOrderDetail>()
  const pagination = ref<DetailPagination>()
  const loading = ref(false)

  const orderStatusUpdated = (data: ShippingUpdateResponse | ShopOrderDetail) => {
    if (shopOrder.value) {
      if ('shop_order' in data) {
        shopOrder.value.order_status = data.shop_order.order_status
      }
      shopOrder.value = {
        ...shopOrder.value,
        ...data,
        user: shopOrder.value.user,
      }
    }
  }

  const getData = async () => {
    try {
      loading.value = true
      const response = await api.GET<ApiResource<ShopOrderDetail>>(`admin/shop-order/${route.params.id}`)
      shopOrder.value = response.data
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  return {
    shopOrder,
    loading,
    pagination,
    getData,
    orderStatusUpdated,
  }
}

export function useShopOrderUpdateTrackingNumber () {
  const api = useApi()
  const loading = ref(false)
  const errors = ref<FormError>({})
  const toast = useToastStore()

  const form = gReactive({
    tracking_number: '',
  })

  const submit = async (
    orderId: number,
    callback?: (data: ShopOrderDetail) => void,
  ) => {
    try {
      loading.value = true
      const payload = form.tracking_number ? { tracking_number: form.tracking_number } : {}
      const response = await api.POST<ShopOrderDetail>(`admin/shop-order/${orderId}/tracking-number`, payload)

      if (callback) {
        callback(response)
      }

      toast.add(form.tracking_number ? 'Status pesanan ditandai dikirim' : 'Status pesanan ditandai diproses')
    } catch (error) {
      errors.value = api.formErrors(error)
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    errors,
    form,
    submit,
  }
}

export function useShopOrderMarkReceived () {
  const api = useApi()
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const toast = useToastStore()

  const submit = async (orderId: number) => {
    try {
      submitting.value = true
      await api.POST<ShopOrderDetail>(`admin/shop-order/${orderId}/received`)
      toast.add('Status pesanan ditandai diterima')
    } catch (error) {
      errors.value = api.formErrors(error)
    } finally {
      submitting.value = false
    }
  }

  return {
    submitting,
    errors,
    submit,
  }
}

